import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Minus } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";

const SPECS = [
  { match: "PAX-1", zone: "Dos / lombaires", type: "Passif", poids: "2,9 kg", assist: "Jusqu'à 30 % d'effort en moins", ideal: "Manutentions répétées, logistique, GD" },
  { match: "PAX-2 ACTIVE", zone: "Dos / lombaires", type: "Actif motorisé", poids: "6,5 kg", assist: "Jusqu'à 25 kg de compensation", ideal: "Manutention lourde intensive" },
  { match: "ARM-ASSIST", zone: "Bras / épaules", type: "Passif", poids: "3,4 kg", assist: "Compense le poids des bras + outillage", ideal: "Travail bras levés : assemblage, peinture" },
  { match: "NECK-RELIEF", zone: "Nuque / cervicales", type: "Passif", poids: "1,8 kg", assist: "−40 % d'activité des muscles cervicaux", ideal: "Travail tête levée : plafonds, inspection" },
  { match: "LEG-CHAIR", zone: "Jambes / genoux", type: "Passif", poids: "3,2 kg", assist: "Assise verrouillable n'importe où", ideal: "Postes debout statiques ou accroupis" },
  { match: "GRIP-ASSIST", zone: "Main / préhension", type: "Actif souple", poids: "0,6 kg", assist: "Amplifie la force de serrage", ideal: "Force de main réduite, gestes répétitifs" },
  { match: "FULL-FRAME", zone: "Corps entier", type: "Passif structurel", poids: "12 kg", assist: "Transfert des charges vers le sol", ideal: "Charges lourdes : fonderie, naval, aéro" },
];

const ROWS = [
  ["Zone soutenue", (s) => s.zone],
  ["Type", (s) => s.type],
  ["Poids", (s) => s.poids],
  ["Assistance", (s) => s.assist],
  ["Location mensuelle", () => "Sur devis"],
  ["Aide inclusion / RQTH", () => <Check size={18} style={{ color: "var(--orange)" }} />],
  ["Idéal pour", (s) => s.ideal],
];

export default function ComparateurExosquelettes() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/products").then((r) => {
      const exos = r.data.filter((p) => p.subcategory === "Exosquelettes & assistance physique");
      const rows = [];
      for (const spec of SPECS) {
        const prod = exos.find((p) => p.name.includes(spec.match));
        if (prod) rows.push({ ...spec, prod });
      }
      setItems(rows);
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="comparateur-exo-page" className="pt-[148px]">
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
        <Reveal><p className="overline mb-4" style={{ color: "var(--orange)" }}>Guide de choix</p></Reveal>
        <Reveal delay={0.05}>
          <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tight leading-none">Comparateur d'exosquelettes</h1>
          <p className="mt-6 text-lg max-w-2xl" style={{ color: "var(--ink-2)" }}>
            Chaque zone du corps a son exosquelette. Tous nos modèles sont disponibles à la location mensuelle pour tester avant d'investir, avec étude de poste par nos ergonomes.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 overflow-x-auto" data-lenis-prevent style={{ border: "1px solid var(--border)" }} data-testid="compare-exo-table-wrapper">
            <table className="w-full text-sm" style={{ minWidth: `${180 + items.length * 190}px`, borderCollapse: "collapse" }} data-testid="compare-exo-table">
              <thead>
                <tr>
                  <th className="p-4 text-left align-bottom sticky left-0 z-10" style={{ background: "var(--white)", borderBottom: "2px solid var(--orange)", minWidth: "160px" }}>
                    <span className="overline" style={{ color: "var(--ink-2)" }}>Critères</span>
                  </th>
                  {items.map((it) => (
                    <th key={it.prod.id} className="p-4 text-left align-bottom" style={{ background: "var(--white)", borderBottom: "2px solid var(--orange)", minWidth: "190px" }}>
                      <Link to={`/catalogue/${it.prod.id}`} className="block group" data-testid={`compare-exo-head-${it.prod.id}`}>
                        <img src={it.prod.image} alt={it.prod.name} className="w-20 h-20 object-cover mb-3 transition-transform duration-500 group-hover:scale-105" style={{ border: "1px solid var(--border)" }} />
                        <span className="font-display font-bold leading-tight block">{it.prod.name}</span>
                        <span className="font-display font-black text-lg mt-1 block" style={{ color: "var(--orange)" }}>
                          Sur devis
                        </span>
                      </Link>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map(([label, fn], ri) => (
                  <tr key={label} style={{ background: ri % 2 ? "var(--white)" : "var(--bone)" }}>
                    <td className="p-4 font-semibold sticky left-0 z-10" style={{ background: ri % 2 ? "var(--white)" : "var(--bone)", borderRight: "1px solid var(--border)" }}>{label}</td>
                    {items.map((it) => (
                      <td key={it.prod.id} className="p-4" style={{ color: "var(--ink-2)" }}>{fn(it)}</td>
                    ))}
                  </tr>
                ))}
                <tr style={{ background: "var(--white)" }}>
                  <td className="p-4 sticky left-0 z-10" style={{ background: "var(--white)" }} />
                  {items.map((it) => (
                    <td key={it.prod.id} className="p-4">
                      <Link to={`/catalogue/${it.prod.id}`} className="btn-outline px-4 py-2 text-xs font-semibold inline-flex items-center gap-1" data-testid={`compare-exo-cta-${it.prod.id}`}>
                        Voir la fiche <ArrowRight size={13} />
                      </Link>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-10 flex flex-col md:flex-row items-center justify-between gap-6 p-8" style={{ background: "var(--ink)", color: "var(--bone)" }} data-testid="rental-banner">
            <div>
              <span className="overline px-2 py-1" style={{ background: "var(--orange)", color: "var(--bone)" }}>Location</span>
              <h2 className="font-display font-black text-2xl mt-3">Testez avant d'acheter — dès 49 €/mois</h2>
              <p className="mt-2 text-sm max-w-xl" style={{ color: "rgba(245,245,240,0.7)" }}>
                Location mensuelle sans engagement, étude de poste et accompagnement à l'acceptation inclus. Le loyer est déduit en cas d'achat.
              </p>
            </div>
            <Link to="/contact?produit=Location d'exosquelette" className="px-8 py-4 font-display font-bold whitespace-nowrap" style={{ background: "var(--orange)", color: "var(--bone)" }} data-testid="rental-banner-cta">
              Demander une location
            </Link>
          </div>
        </Reveal>

        <p className="mt-8 text-sm" style={{ color: "var(--ink-2)" }}>
          Les exosquelettes sont éligibles aux aides AGEFIPH/FIPHFP pour l'insertion des personnes en situation de handicap — <Link to="/inclusion-handicap" className="font-semibold link-hover" style={{ color: "var(--orange)" }}>découvrez notre accompagnement Inclusion</Link>.
        </p>
      </section>
    </div>
  );
}
